import { ErrorText, Input, InputFieldClearButton } from '@presentation/components';
import { hasFieldError } from '@presentation/utils';
import { useField } from 'formik';
import Slider from 'rc-slider';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as Styled from './styled';
import type { InputRangeFieldProps } from './types';

import 'rc-slider/assets/index.css';

export * from './types';

export const InputRangeField: FC<InputRangeFieldProps> = (props) => {
  const { name, limit, onChange, labelFormatter, clearButton, ...rest } = props;
  const [field, meta, helpers] = useField<[number, number]>(name);

  // Optimization to avoid re-rendering the parent component, which is slow for an input like this
  const [tempValue, setTempValue] = useState(field.value);

  const onChangeHandler = useCallback((value: number[]) => {
    const formattedValue: [number, number] = [value[0], value[1]];
    setTempValue(formattedValue as any);
  }, []);

  const onAfterChangeHandler = useCallback(
    (value: number[]) => {
      const formattedValue: [number, number] = [value[0], value[1]];
      helpers.setValue(formattedValue);
      onChange?.(formattedValue);
    },
    [helpers, onChange],
  );

  const isChanged: boolean = useMemo(() => {
    return JSON.stringify(field.value) !== JSON.stringify(limit);
  }, [field.value, limit]);

  const formattedValue = useCallback(
    (value: number): string => {
      return labelFormatter?.(value) ?? value.toString();
    },
    [labelFormatter],
  );

  const valueLabel: string = useMemo(() => {
    if (!isChanged) return '';
    return `${formattedValue(field.value[0])} - ${formattedValue(field.value[1])}`;
  }, [field.value, formattedValue, isChanged]);

  return (
    <>
      <Input
        onBlur={field.onBlur}
        textDisabled
        value={valueLabel}
        {...rest}
        iconRight={
          <InputFieldClearButton<[number, number]>
            value={field.value}
            initialValue={meta.initialValue}
            multiple={false}
            {...clearButton}
            onClear={(newValue) => {
              helpers.setValue(newValue as any);
              clearButton?.onClear?.(newValue as any);
              setTempValue(newValue as any);
            }}
          >
            {rest.iconRight}
          </InputFieldClearButton>
        }
        dropdown={
          <Styled.RangeContainer>
            <Styled.RangeLabelContainer>
              {tempValue.map((value) => (
                <div key={`${Math.random()}-${value}`}>{formattedValue(value)}</div>
              ))}
            </Styled.RangeLabelContainer>
            <Slider
              range
              value={tempValue}
              onChange={onChangeHandler}
              onAfterChange={onAfterChangeHandler}
              min={limit[0]}
              max={limit[1]}
            />
          </Styled.RangeContainer>
        }
      />
      {hasFieldError(meta) && <ErrorText>{meta.error}</ErrorText>}
    </>
  );
};
